import React, { useState, useRef, useEffect } from "react";
import {
  TextField,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  Button,
  InputAdornment,
  Popper,
  ClickAwayListener,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { getExamsMutiSearch } from "../../services/ExamService";

const MultiSearchInput = ({
  childExamIDs,
  setChildExamIDs,
  selectedExams,
  setSelectedExams,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredExams, setFilteredExams] = useState([]);
  const inputRef = useRef(null);
  const [examData, setExamData] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      const response = await getExamsMutiSearch();
      if (response && response.data) {
        setExamData(response.data);
      }
    };
    fetchData();
  }, []);
  // Filter exams based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredExams([]);
      setIsOpen(false);
      return;
    }

    const filtered = examData.filter(
      (exam) =>
        exam.title.text.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !childExamIDs.includes(exam._id)
    );
    setFilteredExams(filtered);
    setIsOpen(filtered.length > 0);
  }, [searchTerm, childExamIDs]);

  const addExam = (exam) => {
    const examId = exam._id;

    // Thêm _id vào mảng childExamIDs
    setChildExamIDs((prev) => {
      const newIds = [...prev, examId];
      return newIds;
    });

    // Thêm exam object vào mảng để hiển thị
    setSelectedExams((prev) => [...prev, exam]);

    setSearchTerm("");
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const removeExam = (examId) => {
    setChildExamIDs((prev) => {
      const newIds = prev.filter((id) => id !== examId);
      return newIds;
    });

    setSelectedExams((prev) => prev.filter((exam) => exam._id !== examId));
  };

  const clearAll = () => {
    setChildExamIDs([]);
    setSelectedExams([]);
    setSearchTerm("");

    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Backspace" &&
      searchTerm === "" &&
      selectedExams.length > 0
    ) {
      const lastExam = selectedExams[selectedExams.length - 1];
      removeExam(lastExam._id);
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 800, mx: "auto" }}>
      <Typography
        variant="h6"
        gutterBottom
        className="text-[14px] text-[#5f6368]"
      >
        Tìm kiếm và thêm đề thi *
      </Typography>

      {/* Search Input */}
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: "relative" }}>
          <TextField
            ref={inputRef}
            fullWidth
            placeholder="Tìm kiếm đề thi theo tên..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            // onKeyDown={handleKeyDown}
            onFocus={() => {
              const filtered = examData.filter(
                (exam) =>
                  exam.title.text
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) &&
                  !childExamIDs.includes(exam._id)
              );
              setFilteredExams(filtered);
              setIsOpen(filtered.length > 0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />

          {/* Dropdown Results */}
          <Popper
            open={isOpen && filteredExams.length > 0}
            anchorEl={inputRef.current}
            placement="bottom-start"
            style={{ width: inputRef.current?.offsetWidth, zIndex: 1300 }}
          >
            <Paper elevation={3} sx={{ maxHeight: 300, overflow: "auto" }}>
              <List dense>
                {filteredExams.map((exam) => (
                  <ListItem key={exam._id} disablePadding>
                    <ListItemButton onClick={() => addExam(exam)}>
                      <ListItemText
                        primary={exam.title.text}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              Môn: {exam.subject}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {exam.numberOfQuestions} câu hỏi • {exam.time}{" "}
                              phút
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                      <AddIcon color="action" fontSize="small" />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>

      {/* Summary */}
      {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Đã chọn: <strong>{selectedExams.length}</strong> đề thi
      </Typography> */}

      {/* Selected Exams List */}
      {selectedExams.length > 0 && (
        <Paper elevation={1} sx={{ mt: 2, mb: 3 }}>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", pt: 2, pr: 2 }}
          >
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={clearAll}
              startIcon={<ClearIcon />}
            >
              Xóa tất cả
            </Button>
          </Box>
          <Typography variant="subtitle2" sx={{ p: 2, pb: 1 }}>
            Danh sách đề thi đã chọn:
          </Typography>
          <List dense>
            {selectedExams.map((exam, index) => (
              <ListItem
                key={exam._id}
                secondaryAction={
                  <Button
                    size="small"
                    color="error"
                    onClick={() => removeExam(exam._id)}
                  >
                    <ClearIcon fontSize="small" />
                  </Button>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {index + 1}.
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {exam.title.text}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      ID: {exam._id} • {exam.subject} • {exam.numberOfQuestions}{" "}
                      câu • {exam.time} phút
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default MultiSearchInput;
